import {
  ErrorNotifier,
  errorDataBuilder,
} from "../../../api/src/utils/error-notifier";

import { VinoUser } from "./state";
import config from "../../../config.json";
import vinoFetch from "../../utils/vinoFetch";
import { AppMetadata, ChargebeeCustomer } from "../../../api/src/types";

const market = process.env.GATSBY_VM_MARKET;
const configData = config[market];

interface AuthResolverInterface {
  validateEmail: (email: string) => Promise<{ exists: boolean; error?: Error }>;
  updateProfile: (
    payload: {
      given_name?: string;
      family_name?: string;
      phone?: string;
      birthdate?: string;
    },
    tokenid: string
  ) => Promise<{ user?: VinoUser; error?: Error }>;
  changeEmailStepTwo: (
    payload: { email: string },
    token: string
  ) => Promise<{ error?: Error }>;
  changeEmailStepThree: (
    payload: { email: string; otp: string },
    token: string
  ) => Promise<{ error?: Error }>;
  hasSocialLogin: (
    email: string
  ) => Promise<{ isSocial: boolean; error?: Error }>;
  getChargebeeCustomerDetails: (
    tokenid: string
  ) => Promise<ChargebeeCustomer | undefined>;
  updateAppMetadata: (
    app_metadata: AppMetadata,
    tokenid: string
  ) => Promise<{ user?: VinoUser; error?: Error }>;
  getLatestUserData: (
    tokenid: string
  ) => Promise<VinoUser | undefined>;
}

export const AuthResolver: AuthResolverInterface = {
  validateEmail: async (email) => {
    const rootOrigin = window.location.origin;
    try {
      const { data } = await vinoFetch.post(
        `${rootOrigin}/identity/api/validate-email`,
        {
          email,
        }
      );

      return { exists: data?.status };
    } catch (error) {
      const statusCode = error.response ? error.response.status : 400;
      ErrorNotifier.notify(error, errorDataBuilder("FE", statusCode, error));
      return { error };
    }
  },
  updateProfile: async (payload, tokenid) => {
    try {
      const { data } = await vinoFetch.get("/api/update-auth0-user-metadata", {
        params: {
          given_name: payload?.given_name,
          family_name: payload?.family_name,
          user_metadata: JSON.stringify(payload),
        },
        headers: {
          authorization: `Bearer ${tokenid}`,
        },
      });

      return { user: data.data };
    } catch (error) {
      const statusCode = error.response ? error.response.status : 400;
      ErrorNotifier.notify(error, errorDataBuilder("FE", statusCode, error));
      return { error };
    }
  },
  hasSocialLogin: async (
    email: string
  ): Promise<{ isSocial?: boolean; error?: Error }> => {
    try {
      const response = await vinoFetch.post(
        `${
          configData.IDENTITY_HOST[process.env.GATSBY_VM_ENV]
        }/api/check-social-identity`,
        {
          email,
        }
      );
      if (response.status === 200) {
        return { isSocial: response.data?.isSocial };
      } else {
        throw new Error(response.data.error);
      }
    } catch (error) {
      console.error(error);
      return { isSocial: false };
    }
  },
  changeEmailStepTwo: async (payload, token) => {
    try {
      await vinoFetch.post(
        `${
          configData.IDENTITY_HOST[process.env.GATSBY_VM_ENV]
        }/api/change-email__step-2`,
        payload,
        {
          headers: {
            "content-type": "application/json",
            authorization: `Bearer ${token}`,
          },
        }
      );

      return {};
    } catch (error) {
      if (error.response.status === 409) {
        const message =
          error.response?.data?.message ?? "Sorry, something went wrong!";
        ErrorNotifier.notify(error, errorDataBuilder("FE", 409, error));
        return {
          error: {
            message,
          },
        };
      }
      const statusCode = error.response ? error.response.status : 400;
      ErrorNotifier.notify(error, errorDataBuilder("FE", statusCode, error));
      return { error };
    }
  },
  changeEmailStepThree: async (payload, token) => {
    try {
      await vinoFetch.post(
        `${
          configData.IDENTITY_HOST[process.env.GATSBY_VM_ENV]
        }/api/change-email__step-3`,
        payload,
        {
          headers: {
            "content-type": "application/json",
            authorization: `Bearer ${token}`,
          },
        }
      );

      return {};
    } catch (error) {
      const statusCode = error.response ? error.response.status : 400;
      ErrorNotifier.notify(error, errorDataBuilder("FE", statusCode, error));
      return { error };
    }
  },
  getChargebeeCustomerDetails: async (token: unknown): Promise<any> => {
    const tokenId = token;
    const url = "/api/get-chargebee-customer-by-email";
    const headers = {
      Authorization: `Bearer ${tokenId}`,
    };
    try {
      const { data } = await vinoFetch.get(url, {
        headers,
        params: {},
      });
      return data;
    } catch (error) {
      const statusCode = error.response ? error.response.status : 400;
      ErrorNotifier.notify(error, errorDataBuilder("FE", statusCode, error));
      return { error };
    }
  },
  updateAppMetadata: async (app_metadata, tokenid) => {
    try {
      const { data } = await vinoFetch.post(
        "/api/update-auth0-app-metadata",
        {
          app_metadata: app_metadata,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenid}`,
          },
        }
      );
      return data;
    } catch (error) {
      const statusCode = error?.response?.status || 500;
      const message = error?.message || "Unknown error";

      ErrorNotifier.notify(error, errorDataBuilder("FE", statusCode, error));

      return {
        success: false,
        statusCode,
        error: message,
      };
    }
  },
  getLatestUserData: async (token: unknown) => {
    try {
      const accessToken = token;
      const {
        data: { data: user },
      } = await vinoFetch.get("/api/get-auth0-user-with-metadata", {
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      });
      return user;
    } catch (error) {
      const statusCode = error.response ? error.response.status : 400;
      ErrorNotifier.notify(error, errorDataBuilder("FE", statusCode, error));
      return undefined;
    }
  },
};
